// The lead model's tokenizer for the skill-index token count (10 DC-02; 02
// `agent_template_version.skill_index_tokens`, "measured at validation with the lead model's
// tokenizer"). Exact where a tokenizer for the model can run locally, otherwise a documented
// upper bound: the count is never lower than the real one, so a version under the cap here is
// under the cap for the model.
//
// - OpenAI model names: exact, with `gpt-tokenizer` (MIT, pinned 4.0.0): `o200k_base` for the
//   GPT-4o / GPT-4.1 / GPT-4.5 / GPT-5 / o-series families, `o200k_harmony` for `gpt-oss-*`,
//   `cl100k_base` for `gpt-4` / `gpt-3.5`. Special-token text (`<|endoftext|>`) is counted as
//   ordinary text, as it is when the index is sent inside a prompt.
// - Everything else, Claude included (Anthropic publishes no local tokenizer for current models):
//   `utf8-bytes-upper-bound`, the UTF-8 byte length. Every byte-level BPE token covers at least
//   one byte, so no such tokenizer produces more tokens than the text has bytes. It is strict
//   (2 000 bytes at the default cap), never an under-count. The API may replace it with an exact
//   count from the gateway (`measuredCounter`) when the gateway can count for that model.
//
// The name in `role_model_map` is the gateway model name. Only a name that is itself an OpenAI
// model name (optionally `openai/` or `azure/` prefixed) gets an exact tokenizer, so an unknown
// alias falls back to the upper bound. The gateway must not route an OpenAI model name to
// another vendor's model (README §Token cap).
import { countTokens as countCl100k } from "gpt-tokenizer/encoding/cl100k_base";
import { countTokens as countO200k } from "gpt-tokenizer/encoding/o200k_base";
import { countTokens as countHarmony } from "gpt-tokenizer/encoding/o200k_harmony";
import type { RoleModelMap } from "../schemas";
import { type TokenCounter, utf8ByteLength } from "./token-counter";

/** Special tokens are ordinary text in a skill index: never refused, never a single token. */
const AS_TEXT = { disallowedSpecial: new Set<string>() };

export type LeadTokenizer = {
  counter: TokenCounter;
  /** True when the count is the model's own tokenizer, false for the upper bound. */
  exact: boolean;
};

/** The documented conservative count: UTF-8 bytes, an upper bound for byte-level BPE. */
export const utf8BytesUpperBound: TokenCounter = Object.freeze({
  name: "utf8-bytes-upper-bound",
  count: (text: string) => utf8ByteLength(text),
});

const exact = (encoding: string, count: (t: string, o: typeof AS_TEXT) => number) =>
  Object.freeze({
    name: `gpt-tokenizer@4.0.0/${encoding}`,
    count: (text: string) => count(text, AS_TEXT),
  }) satisfies TokenCounter;

const O200K = exact("o200k_base", countO200k);
const HARMONY = exact("o200k_harmony", countHarmony);
const CL100K = exact("cl100k_base", countCl100k);

/** Model-name families with an exact local tokenizer. First match wins. */
const FAMILIES: readonly { re: RegExp; counter: TokenCounter }[] = [
  { re: /^gpt-oss-/, counter: HARMONY },
  {
    re: /^(?:gpt-5|gpt-4\.1|gpt-4\.5|gpt-4o|chatgpt-4o|o1|o3|o4|codex-mini)(?:$|[-.])/,
    counter: O200K,
  },
  { re: /^(?:gpt-4|gpt-3\.5)(?:$|-)/, counter: CL100K },
];

/** The exact local tokenizer for `model`, or null when there is none. */
export function exactTokenizerFor(model: string): TokenCounter | null {
  const name = model
    .trim()
    .toLowerCase()
    .replace(/^(?:openai|azure)\//, "");
  return FAMILIES.find((f) => f.re.test(name))?.counter ?? null;
}

/** The lead model's tokenizer: exact when available, the UTF-8 byte bound otherwise. */
export function leadTokenizerFor(model: string): LeadTokenizer {
  const counter = exactTokenizerFor(model);
  return counter ? { counter, exact: true } : { counter: utf8BytesUpperBound, exact: false };
}

/** The role whose model counts the skill index (ADR-0018 §3 `runtime.model_alias`). */
export const LEAD_ROLE = "lead";

/**
 * The lead model of a role-to-model map: the `lead` role, or the only role when there is one.
 * Null when neither applies (validation then reports `lead_model_missing`).
 */
export function leadModelOf(map: RoleModelMap): string | null {
  const lead = Object.hasOwn(map, LEAD_ROLE) ? map[LEAD_ROLE] : undefined;
  if (lead) return lead.model;
  const roles = Object.keys(map);
  return roles.length === 1 ? (map[roles[0] as string]?.model ?? null) : null;
}

/**
 * A counter for one text counted elsewhere (the gateway's count, which is asynchronous). It
 * answers only for that exact text and throws for any other, which validation reports as
 * `token_count_invalid`, so a count can never be applied to a different index.
 */
export function measuredCounter(name: string, text: string, count: number): TokenCounter {
  return Object.freeze({
    name,
    count: (t: string) => {
      if (t !== text) throw new Error(`${name} measured a different text`);
      return count;
    },
  });
}
